import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import { getCampaignDrafts, type ClientDraftItem } from '@/api/beatlyApi';

export interface UseDraftsParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/**
 * Give up on GET /drafts after this long and show an error with Retry. Big campaigns
 * (100+ drafts) used to leave Content Review on skeletons for 90–140 s while the gateway
 * timed out (and the transport retry started the whole request again). 120 s, not less: on
 * 25 Sep, with the first backend fix live, RevolutionRace's 131 drafts took 50–71 s but did
 * load; a shorter limit would turn a slow success into an error until the next fix ships.
 */
export const DRAFTS_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Drafts are expensive to build server-side, so a remount or regaining focus within a few minutes
 * reuses the cache (focus refetch respects staleTime). Approve/decline refetch explicitly.
 */
export const DRAFTS_STALE_TIME_MS = 5 * 60 * 1000;

/** Stable empty list: a fresh `[]` per render re-runs every memo keyed on drafts (see useCuration). */
const EMPTY_DRAFTS: ClientDraftItem[] = [];

/** API expects campaign IDs like "123456-cpg"; avoid calling with invalid ids to prevent 404/500. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

export function draftsQueryKey(campaignId: string | undefined) {
  return ['client', 'campaign', campaignId, 'drafts'] as const;
}

/** Shared by `useDrafts` and `prefetchCampaignDrafts` so the two can never drift. */
export function draftsQueryOptions(campaignId: string, token: string) {
  return {
    queryKey: draftsQueryKey(campaignId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getCampaignDrafts(campaignId, token, { signal, timeoutMs: DRAFTS_REQUEST_TIMEOUT_MS }),
    staleTime: DRAFTS_STALE_TIME_MS,
    // A timed-out drafts call should surface Retry, not silently start another full wait. It must
    // live here, not only in useDrafts: react-query fixes `retry` when a fetch starts, so a
    // Dashboard hover prefetch would otherwise run with the app default (retry once).
    retry: false,
  };
}

/**
 * Warm the drafts cache when the user shows clear intent to open Content Review
 * (e.g. hovering a "Review drafts" link). No-op when fresh data is already cached.
 */
export function prefetchCampaignDrafts(queryClient: QueryClient, campaignId: string | undefined): void {
  const token = getTokenFromStorage();
  if (!token || !campaignId || !isValidCampaignId(campaignId)) return;
  void queryClient.prefetchQuery(draftsQueryOptions(campaignId, token));
}

/**
 * Fetches drafts (content approval) for a campaign.
 * GET /app/v2/client/campaign/:campaignId/drafts
 */
export function useDrafts({ campaignId, enabled = true }: UseDraftsParams) {
  const token = getTokenFromStorage();
  const shouldFetch = enabled && Boolean(token && campaignId) && isValidCampaignId(campaignId);

  const { data, isLoading, isFetching, isFetched, error, isError, refetch } = useQuery({
    ...draftsQueryOptions(campaignId ?? '', token ?? ''),
    enabled: shouldFetch,
  });

  return {
    data: data ?? EMPTY_DRAFTS,
    /** True once drafts loaded successfully (stays true when a later refetch fails). */
    hasData: data !== undefined,
    isLoading,
    isFetching,
    isFetched,
    error,
    isError,
    refetch,
  };
}
